/**
 * State + mutations for the Auth Letter Payer Config page.
 *
 * Owns the single section-first Firestore config doc (loaded from the primary
 * org) and exposes payer-scoped mutations. Upload/add/sync may fan out the
 * primary prompt content while preserving each target's On/Off placement;
 * toggle relocates each target org's own prompt instead.
 * See {@link module:authLetterConfigUtils} for the doc shape and the
 * `disabled` convention.
 * @module useAuthLetterPayerConfig
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthLetterAgentConfigService } from "../../../../api/services/authLetterAgentConfigService";
import {
  comparePayers,
  deepClone,
  Dict,
  DISABLED_KEY,
  getPayers,
  payerSection,
  SECTIONS,
} from "./authLetterConfigUtils";
import { UploadedPrompt } from "./components/PayerConfigCard";
import { togglePayerAcrossOrgs } from "./togglePayerAcrossOrgs";
import { uploadPromptAcrossOrgs } from "./uploadPromptAcrossOrgs";

/** Everything the page component needs to render and mutate payer config. */
export interface UseAuthLetterPayerConfig {
  /** Raw config document (source of truth for per-payer reads in render). */
  doc: Dict;
  /** All payers to show — persisted plus local-only drafts, sorted. */
  payers: string[];
  /** Lowercased set of persisted payer names (drafts excluded). */
  savedPayerKeys: Set<string>;
  /** `true` while the initial config load is in flight. */
  loading: boolean;
  /** Name of the payer currently being saved, or `null`. */
  savingPayer: string | null;
  /** Toggles a payer On/Off across the given orgs, relocating its prompt. */
  togglePayer: (
    payer: string,
    enabled: boolean,
    targetOrgIds: string[],
  ) => Promise<void>;
  /** Saves an uploaded prompt; `true` if at least one org write succeeded. */
  uploadPrompt: (
    payer: string,
    parsed: UploadedPrompt,
    targetOrgIds: string[],
  ) => Promise<boolean>;
  /** Adds a payer; `true` if accepted / at least one org write succeeded. */
  addPayer: (
    name: string,
    copyFrom: string,
    targetOrgIds: string[],
  ) => Promise<boolean>;
  /**
   * Copies the primary org's prompt content for a payer to the given orgs,
   * preserving each target's On/Off placement. `true` if at least one write
   * succeeded.
   */
  syncPayerToOrgs: (payer: string, targetOrgIds: string[]) => Promise<boolean>;
}

/**
 * Loads and mutates the auth-letter payer config for one primary org, with
 * optional multi-org fan-out on each save.
 *
 * @param orgId - Primary org whose config doc is read for the UI
 * @param orgLabelById - Map of org ID → display name for success/error toasts
 * @return Config state and payer-scoped mutation handlers
 */
export const useAuthLetterPayerConfig = (
  orgId: string,
  orgLabelById: Record<string, string> = {},
): UseAuthLetterPayerConfig => {
  const [doc, setDoc] = useState<Dict>({});
  const [draftPayers, setDraftPayers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingPayer, setSavingPayer] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      setDoc(await AuthLetterAgentConfigService.getRawConfig(orgId));
    } catch {
      toast.error("Failed to load auth letter payer config.");
      setDoc({});
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const savedPayers = useMemo(() => getPayers(doc), [doc]);
  const savedPayerKeys = useMemo(
    () => new Set(savedPayers.map((p) => p.toLowerCase())),
    [savedPayers],
  );
  const payers = useMemo(() => {
    const drafts = draftPayers.filter(
      (p) => !savedPayerKeys.has(p.toLowerCase()),
    );
    return [...savedPayers, ...drafts].sort(comparePayers);
  }, [savedPayers, savedPayerKeys, draftPayers]);

  const resolveOrgLabel = (id: string): string => orgLabelById[id] ?? id;

  /**
   * Builds a short success toast describing how many orgs were updated.
   *
   * @param payer - Payer name for the message
   * @param action - Past-tense action phrase (e.g. `turned on`)
   * @param targetOrgIds - Orgs that received the write
   */
  const successToast = (
    payer: string,
    action: string,
    targetOrgIds: string[],
  ) => {
    const count = targetOrgIds.length;
    const labels =
      count <= 5
        ? targetOrgIds.map(resolveOrgLabel).join(", ")
        : `${targetOrgIds
            .slice(0, 3)
            .map(resolveOrgLabel)
            .join(", ")} +${count - 3} more`;
    toast.success(
      `"${payer}" ${action} · saved to ${count} org${count === 1 ? "" : "s"} (${labels}).`,
    );
  };

  /**
   * Toggles a payer On/Off in each selected org by relocating that org's own
   * prompt between top level and `disabled`. Never copies another org's content.
   *
   * @param payer - Payer to toggle
   * @param enabled - `true` to enable (move to top level), `false` to disable
   * @param targetOrgIds - Orgs whose own prompts should be relocated
   */
  const togglePayer = async (
    payer: string,
    enabled: boolean,
    targetOrgIds: string[],
  ) => {
    // The `default` prompt is the fallback target — disabling it would break
    // every payer, so it must always stay enabled (top level).
    if (!enabled && payer.trim().toLowerCase() === "default") {
      toast.error("The Default prompt can't be turned off.");
      return;
    }
    if (targetOrgIds.length === 0) {
      toast.error("Select at least one organization.");
      return;
    }

    setSavingPayer(payer);
    try {
      const { updatedOrgIds, skippedOrgIds, failures, primaryNextDoc } =
        await togglePayerAcrossOrgs({
          payer,
          enabled,
          targetOrgIds,
          primaryOrgId: orgId,
          primaryDoc: doc,
        });

      if (primaryNextDoc) setDoc(primaryNextDoc);

      if (failures.length > 0) {
        const detail = failures
          .map((f) => `${resolveOrgLabel(f.orgId)}: ${f.message}`)
          .join("; ");
        toast.error(`Failed to toggle "${payer}" on some orgs — ${detail}`);
      }
      if (
        skippedOrgIds.length > 0 &&
        updatedOrgIds.length === 0 &&
        failures.length === 0
      ) {
        toast.error(
          `"${payer}" has no prompt to toggle in the selected organizations.`,
        );
        return;
      }
      if (skippedOrgIds.length > 0 && updatedOrgIds.length > 0) {
        const skippedLabels = skippedOrgIds.map(resolveOrgLabel).join(", ");
        toast.error(
          `"${payer}" had no prompt to toggle in: ${skippedLabels}. Those orgs were left unchanged.`,
        );
      }
      if (updatedOrgIds.length > 0) {
        successToast(payer, `turned ${enabled ? "on" : "off"}`, updatedOrgIds);
      }
    } finally {
      setSavingPayer(null);
    }
  };

  /**
   * Saves an uploaded prompt for a payer across selected orgs.
   *
   * Each org keeps its own On/Off placement; the opposite path is cleared so
   * uploads never leave a dead duplicate or silently re-enable a payer.
   *
   * @param payer - Payer receiving the prompt
   * @param parsed - Uploaded prompt sections
   * @param targetOrgIds - Orgs that should receive this upload
   * @return `true` when at least one org write succeeded (partial counts)
   */
  const uploadPrompt = async (
    payer: string,
    parsed: UploadedPrompt,
    targetOrgIds: string[],
  ): Promise<boolean> => {
    if (targetOrgIds.length === 0) {
      toast.error("Select at least one organization.");
      return false;
    }

    setSavingPayer(payer);
    try {
      const { updatedOrgIds, failures, primaryNextDoc } =
        await uploadPromptAcrossOrgs({
          payer,
          parsed,
          targetOrgIds,
          primaryOrgId: orgId,
          primaryDoc: doc,
        });

      if (primaryNextDoc) {
        setDoc(primaryNextDoc);
        // Only drop the draft when the primary (UI) org actually persisted it.
        setDraftPayers((drafts) => drafts.filter((p) => p !== payer));
      }

      if (failures.length > 0) {
        const detail = failures
          .map((f) => `${resolveOrgLabel(f.orgId)}: ${f.message}`)
          .join("; ");
        toast.error(`Failed to upload "${payer}" to some orgs — ${detail}`);
      }
      if (updatedOrgIds.length === 0) {
        if (failures.length === 0) {
          toast.error(`Nothing to upload for "${payer}".`);
        }
        return false;
      }
      // Partial success still counts as saved for the UI / draft lifecycle.
      successToast(payer, "prompt uploaded", updatedOrgIds);
      return true;
    } finally {
      setSavingPayer(null);
    }
  };

  /**
   * Creates a payer, optionally copying another payer's prompt. With nothing to
   * copy, it's added as a local-only draft rather than an empty stub.
   *
   * Add-with-copy fans out via {@link uploadPromptAcrossOrgs} so each target
   * keeps correct On/Off placement (no dead duplicates). Target orgs are
   * scanned first — if the name already exists anywhere selected, nothing is
   * written and the collision is surfaced.
   *
   * @param name - New payer name
   * @param copyFrom - Payer whose prompt to copy, or empty for a blank payer
   * @param targetOrgIds - Orgs that should receive the new payer when persisted
   * @return `true` when the name passed validation and was accepted
   */
  const addPayer = async (
    name: string,
    copyFrom: string,
    targetOrgIds: string[],
  ): Promise<boolean> => {
    if (name.trim() === DISABLED_KEY) {
      toast.error(`"${DISABLED_KEY}" is a reserved name.`);
      return false;
    }
    if (payers.some((p) => p.toLowerCase() === name.toLowerCase())) {
      toast.error(`Payer "${name}" already exists.`);
      return false;
    }

    const sections: Partial<Record<(typeof SECTIONS)[number], Dict>> = {};
    if (copyFrom) {
      for (const sec of SECTIONS) {
        const source = payerSection(doc, sec, copyFrom);
        if (Object.keys(source).length > 0) sections[sec] = deepClone(source);
      }
    }

    // Nothing to persist yet — keep the payer local until a prompt is uploaded.
    if (Object.keys(sections).length === 0) {
      setDraftPayers((drafts) => [...drafts, name]);
      toast.success(`Added "${name}". Upload its prompt JSON to save it.`);
      return true;
    }

    if (targetOrgIds.length === 0) {
      toast.error("Select at least one organization.");
      return false;
    }

    setSavingPayer(name);
    try {
      const uniqueIds = Array.from(
        new Set(targetOrgIds.map((id) => id.trim()).filter(Boolean)),
      );
      const nameKey = name.toLowerCase();
      const collisions: string[] = [];
      const readFailures: { orgId: string; message: string }[] = [];
      await Promise.all(
        uniqueIds.map(async (targetId) => {
          try {
            const orgDoc =
              targetId === orgId
                ? doc
                : await AuthLetterAgentConfigService.getRawConfig(targetId);
            if (getPayers(orgDoc).some((p) => p.toLowerCase() === nameKey)) {
              collisions.push(targetId);
            }
          } catch (e) {
            readFailures.push({
              orgId: targetId,
              message: e instanceof Error ? e.message : "Unknown error",
            });
          }
        }),
      );
      if (readFailures.length > 0) {
        const detail = readFailures
          .map((f) => `${resolveOrgLabel(f.orgId)}: ${f.message}`)
          .join("; ");
        toast.error(
          `Could not verify "${name}" in some orgs before adding — ${detail}`,
        );
        return false;
      }
      if (collisions.length > 0) {
        const collisionLabels = collisions.map(resolveOrgLabel).join(", ");
        toast.error(
          `Payer "${name}" already exists in: ${collisionLabels}. Choose a different name or deselect those orgs.`,
        );
        return false;
      }

      const { updatedOrgIds, failures, primaryNextDoc } =
        await uploadPromptAcrossOrgs({
          payer: name,
          parsed: sections,
          targetOrgIds: uniqueIds,
          primaryOrgId: orgId,
          primaryDoc: doc,
        });

      if (primaryNextDoc) setDoc(primaryNextDoc);

      if (failures.length > 0) {
        const detail = failures
          .map((f) => `${resolveOrgLabel(f.orgId)}: ${f.message}`)
          .join("; ");
        toast.error(`Failed to add "${name}" to some orgs — ${detail}`);
      }
      if (updatedOrgIds.length === 0) return false;
      successToast(name, `added from "${copyFrom}"'s prompt`, updatedOrgIds);
      return true;
    } finally {
      setSavingPayer(null);
    }
  };

  /**
   * Copies this payer's primary-org prompt content to the selected orgs.
   *
   * Reuses {@link uploadPromptAcrossOrgs} so each target keeps its own On/Off
   * placement — only the prompt body is overwritten. Local UI state updates
   * only when the primary org is among the targets and its write succeeds.
   *
   * @param payer - Payer whose primary-org prompt is being synced
   * @param targetOrgIds - Orgs that should receive the prompt content
   * @return `true` when at least one org write succeeded (partial counts)
   */
  const syncPayerToOrgs = async (
    payer: string,
    targetOrgIds: string[],
  ): Promise<boolean> => {
    if (targetOrgIds.length === 0) {
      toast.error("Select at least one organization.");
      return false;
    }

    const sections: Partial<Record<(typeof SECTIONS)[number], Dict>> = {};
    for (const sec of SECTIONS) {
      const source = payerSection(doc, sec, payer);
      if (Object.keys(source).length > 0) sections[sec] = deepClone(source);
    }
    if (Object.keys(sections).length === 0) {
      toast.error(`"${payer}" has no prompt to copy.`);
      return false;
    }

    setSavingPayer(payer);
    try {
      const { updatedOrgIds, failures, primaryNextDoc } =
        await uploadPromptAcrossOrgs({
          payer,
          parsed: sections,
          targetOrgIds,
          primaryOrgId: orgId,
          primaryDoc: doc,
        });

      if (primaryNextDoc) setDoc(primaryNextDoc);

      if (failures.length > 0) {
        const detail = failures
          .map((f) => `${resolveOrgLabel(f.orgId)}: ${f.message}`)
          .join("; ");
        toast.error(`Failed to copy "${payer}" to some orgs — ${detail}`);
      }
      if (updatedOrgIds.length === 0) return false;
      successToast(payer, "copied from NYCBS", updatedOrgIds);
      return true;
    } finally {
      setSavingPayer(null);
    }
  };

  return {
    doc,
    payers,
    savedPayerKeys,
    loading,
    savingPayer,
    togglePayer,
    uploadPrompt,
    addPayer,
    syncPayerToOrgs,
  };
};
